/**
 * Elegir grupos de GeoVictoria para sumar al proceso.
 *
 * La lista se pide al abrir y no al cargar la pantalla: es una llamada a un
 * sistema externo, y solo hace falta cuando alguien va a agregar uno. De paso,
 * la pantalla sigue funcionando —sacar un grupo, cambiarle la operación— aunque
 * GeoVictoria esté caído; lo único que no se puede es sumar uno nuevo.
 *
 * Solo aparecen los que todavía no están: ofrecer uno ya agregado sería
 * ofrecer una acción sin efecto.
 *
 * El buscador no es un lujo. La cuenta tiene decenas de grupos con nombres
 * que se parecen mucho entre sí —"SUC_MAL - Administración" y
 * "SUC_MAL -Operaciones"—, y elegir el equivocado no da ningún error: ese
 * grupo simplemente procesa a quien no corresponde.
 */

import { useEffect, useMemo, useState } from 'react';

import { Loader2, Plus, Search } from 'lucide-react';

import { mensajeDe } from '../../api';
import { Aviso } from '../../components/Aviso';
import { Modal } from '../../components/Modal';
import { leerGruposDisponibles } from '../asistencia/api';

export function DialogoAgregarGrupos({
  abierto,
  onCerrar,
  onAgregar,
}: {
  readonly abierto: boolean;
  readonly onCerrar: () => void;
  readonly onAgregar: (grupos: readonly string[]) => void;
}): React.JSX.Element {
  const [grupos, setGrupos] = useState<readonly string[]>([]);
  const [elegidos, setElegidos] = useState<ReadonlySet<string>>(new Set());
  const [busqueda, setBusqueda] = useState('');
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');

  // Se consulta cada vez que se abre: alguien pudo haber creado un grupo en
  // GeoVictoria hace un minuto, y guardarse la lista lo escondería.
  useEffect(() => {
    if (!abierto) return;
    setElegidos(new Set());
    setBusqueda('');
    setCargando(true);
    leerGruposDisponibles()
      .then((d) => {
        setGrupos(d.grupos);
        setError('');
      })
      .catch((err: unknown) => {
        setGrupos([]);
        setError(mensajeDe(err));
      })
      .finally(() => setCargando(false));
  }, [abierto]);

  const visibles = useMemo(() => {
    const t = busqueda.trim().toLowerCase();
    return t === '' ? grupos : grupos.filter((g) => g.toLowerCase().includes(t));
  }, [grupos, busqueda]);

  const alternar = (grupo: string): void => {
    setElegidos((previos) => {
      const siguiente = new Set(previos);
      if (siguiente.has(grupo)) siguiente.delete(grupo);
      else siguiente.add(grupo);
      return siguiente;
    });
  };

  return (
    <Modal
      abierto={abierto}
      titulo="Agregar grupos al proceso"
      identidad="Los que GeoVictoria tiene y todavía no están"
      onCerrar={onCerrar}
      ancho="normal"
      pie={
        <>
          <button
            type="button"
            disabled={elegidos.size === 0}
            onClick={() => {
              onAgregar([...elegidos]);
              onCerrar();
            }}
          >
            <Plus size={15} aria-hidden="true" />
            {elegidos.size === 0
              ? 'Agregar'
              : `Agregar ${String(elegidos.size)} grupo${elegidos.size === 1 ? '' : 's'}`}
          </button>
          <button type="button" className="plano" onClick={onCerrar}>
            Cancelar
          </button>
        </>
      }
    >
      {error !== '' && <Aviso tono="error">{error}</Aviso>}

      {cargando ? (
        <p className="nota">
          <Loader2 className="girando" size={16} aria-hidden="true" /> Consultando GeoVictoria…
        </p>
      ) : (
        <>
          <label className="campo campo--busqueda">
            <Search size={15} aria-hidden="true" />
            <input
              type="search"
              placeholder="Buscar grupo"
              value={busqueda}
              onChange={(ev) => setBusqueda(ev.target.value)}
            />
          </label>

          {grupos.length === 0 && error === '' && (
            <p className="nota">Todos los grupos de GeoVictoria ya están en el proceso.</p>
          )}

          {grupos.length > 0 && visibles.length === 0 && (
            <p className="nota">{`Ningún grupo coincide con «${busqueda.trim()}».`}</p>
          )}

          <div className="lista-disponibles">
            {visibles.map((grupo) => (
              <label key={grupo} className="grupo-disponible">
                <input
                  type="checkbox"
                  checked={elegidos.has(grupo)}
                  onChange={() => alternar(grupo)}
                />
                {grupo}
              </label>
            ))}
          </div>

          {/*
            Se avisa que falta la operación acá y no al cerrar: enterarse
            recién en la lista, con cinco grupos agregados y cinco selectores
            en rojo, se lee como un error y no como un paso pendiente.
          */}
          {elegidos.size > 0 && (
            <p className="nota">
              Después hay que indicar de qué operación es cada uno: sin eso no se puede guardar.
            </p>
          )}
        </>
      )}
    </Modal>
  );
}
